import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Joi from 'joi';
import moment from 'moment';

import { useUpdateShareCategoryMutation, useCreateShareCategoryMutation } from 'api/main';
import { parseBackendViolations, validateSchema } from 'utils/helpers';
import { useCategoryContext } from './CategoryContext';

import SubcategoryItem from './SubcategoryItem';
import SubcategoryForm from './SubcategoryForm';
import { Loader, CustomTextField, CustomDatePicker, CheckboxLabel, ActionButton } from 'components';
import { Box, Collapse } from '@mui/material';
import { texts } from 'utils/languages/fr';
import * as icons from 'mdi-material-ui';

const initErrors = () => ({
    name: '',
    availableFrom: '',
    availableTo: '',
});

const initForm = () => ({
    name: '',
    private: false,
    availableFrom: null,
    availableTo: null,
    shareSubcategories: [],
});

const schema = {
    name: Joi.string().alphanum().required().messages({
        'string.empty': texts.SAISIR_LE_CHAMP,
        'string.alphanum': texts.CATEGORY_NAME_PATTERN,
    }),
    private: Joi.any(),
    availableFrom: Joi.date().messages({ 'date.base': texts.INCORRECT_INPUT }),
    availableTo: Joi.date()
        .when('availableFrom', {
            is: Joi.exist(),
            then: Joi.date()
                .min(Joi.ref('availableFrom'))
                .messages({ 'date.min': texts.DATE_LESS_THAN }),
            otherwise: Joi.date().allow(null),
        })
        .messages({ 'date.base': texts.INCORRECT_INPUT, 'any.required': texts.SAISIR_LE_CHAMP }),
    shareSubcategories: Joi.any(),
};

const CategoryForm = ({ category, fundId, onCancel }) => {
    const {
        editingSubcategoryId,
        newEditingSubcategoryId,
        newSubcategoryForCategory,
        setEditingSubcategoryId,
        setNewSubcategoryForCategory,
    } = useCategoryContext();

    const [
        updateCategory,
        {
            isLoading: updateLoading,
            isSuccess: updateSuccess,
            error: updateError,
            isError: isUpdateError,
        },
    ] = useUpdateShareCategoryMutation();
    const [
        createCategory,
        {
            isLoading: createLoading,
            isSuccess: createSuccess,
            error: createError,
            isError: isCreateError,
        },
    ] = useCreateShareCategoryMutation();

    const [form, setForm] = useState(initForm());
    const [errors, setErrors] = useState(initErrors());

    const handleInputChange = useCallback(
        field => event => {
            const value =
                event.target.type === 'checkbox' ? event.target.checked : event.target.value;
            setForm(prev => ({ ...prev, [field]: value }));

            // Clear error when user starts typing
            if (errors[field]) {
                setErrors(prev => ({ ...prev, [field]: '' }));
            }
        },
        [errors]
    );

    const handleDateChange = useCallback(
        field => date => {
            setForm(prev => ({ ...prev, [field]: date }));

            // Clear error when user changes date
            if (errors[field]) {
                setErrors(prev => ({ ...prev, [field]: '' }));
            }
        },
        [errors]
    );

    const handleRemoveSubcategory = useCallback(
        index => () => {
            setForm(prev => ({
                ...prev,
                shareSubcategories: prev.shareSubcategories.filter((_, i) => i !== index),
            }));
        },
        []
    );

    const handleAddSubcategory = useCallback(() => {
        setNewSubcategoryForCategory(category?.id || 'new');
    }, [category?.id, setNewSubcategoryForCategory]);

    const handleAddSubcategoryToForm = useCallback(
        subcategoryData => {
            // For unsaved categories, add subcategory to form state
            setForm(prev => ({
                ...prev,
                shareSubcategories: [...prev.shareSubcategories, subcategoryData],
            }));
            setNewSubcategoryForCategory(null);
        },
        [setNewSubcategoryForCategory]
    );

    const handleUpdateSubcategoryInForm = useCallback(
        index => subcategoryData => {
            const updateSubcategories = prevSubcategories => {
                return prevSubcategories.map((sub, i) => (i === index ? subcategoryData : sub));
            };

            setForm(prev => ({
                ...prev,
                shareSubcategories: updateSubcategories(prev.shareSubcategories),
            }));
            setEditingSubcategoryId(null);
        },
        [setEditingSubcategoryId]
    );

    const isAnySubcategoryFormOpen = useMemo(
        () => editingSubcategoryId || newEditingSubcategoryId || newSubcategoryForCategory,
        [editingSubcategoryId, newEditingSubcategoryId, newSubcategoryForCategory]
    );

    const handleSubmit = useCallback(() => {
        // Prevent submission if any subcategory form is open
        if (isAnySubcategoryFormOpen) {
            return;
        }

        const { valid, newErrors } = validateSchema(schema, {
            ...form,
            availableFrom: form.availableFrom?.toDate(),
            availableTo: form.availableTo?.toDate(),
        });

        if (valid) {
            setErrors(initErrors());

            const payload = {
                name: form.name.trim(),
                private: form.private,
                availableFrom: form.availableFrom ? moment(form.availableFrom).toISOString() : null,
                availableTo: form.availableTo ? moment(form.availableTo).toISOString() : null,
                investmentFund: `/api/investment_funds/${fundId}`,
            };

            if (category) {
                updateCategory({ id: category.id, ...payload });
            } else {
                // When creating a category, include subcategories data
                const createPayload = { ...payload, shareSubcategories: form.shareSubcategories };
                createCategory(createPayload);
            }
        } else {
            setErrors(newErrors);
        }
    }, [form, category, fundId, updateCategory, createCategory, isAnySubcategoryFormOpen]);

    useEffect(() => {
        if (category) {
            setForm({
                name: category.name || '',
                private: category.private || false,
                availableFrom: category.availableFrom ? moment(category.availableFrom) : null,
                availableTo: category.availableTo ? moment(category.availableTo) : null,
                shareSubcategories: category.shareSubcategories || [],
            });
        }
    }, [category]);

    // Handle success side effects
    useEffect(() => {
        if (updateSuccess || createSuccess) {
            onCancel(); // Close the form on success
        }
    }, [updateSuccess, createSuccess, onCancel]);

    // Handle error side effects
    useEffect(() => {
        if (isUpdateError || isCreateError) {
            const violations = parseBackendViolations({
                ...updateError?.data,
                ...createError?.data,
            });
            if (violations) {
                setErrors(prev => ({ ...prev, ...violations }));
            }
        }
    }, [isUpdateError, isCreateError, updateError, createError]);

    return (
        <React.Fragment>
            <Box display="flex" flexDirection="column" width="100%">
                <CustomTextField
                    label={texts.CATEGORY_NAME}
                    value={form.name}
                    onChange={handleInputChange('name')}
                    error={texts.SHARE_CATEGORY_EXIST(errors.name)}
                    fullWidth
                />
                <CheckboxLabel
                    sx={{ alignItems: 'center' }}
                    checked={form.private}
                    onChange={handleInputChange('private')}
                    label={texts.PRIVATE}
                />
                <CustomDatePicker
                    label={texts.AVAILABLE_FROM}
                    value={form.availableFrom}
                    onChange={handleDateChange('availableFrom')}
                    error={errors.availableFrom}
                    showError
                    fullWidth
                />
                <CustomDatePicker
                    label={texts.AVAILABLE_TO}
                    value={form.availableTo}
                    minDate={form.availableFrom}
                    onChange={handleDateChange('availableTo')}
                    error={errors.availableTo}
                    showError
                    fullWidth
                />
                {form.shareSubcategories.map((subcategory, index) => (
                    <SubcategoryItem
                        key={subcategory.id || `subcategory-${index}`}
                        subcategory={subcategory}
                        onRemove={handleRemoveSubcategory(index)}
                        onUpdate={handleUpdateSubcategoryInForm(index)}
                        categoryId={category?.id}
                    />
                ))}
                {!isAnySubcategoryFormOpen && (
                    <ActionButton
                        sx={{ alignSelf: 'center', marginY: 1 }}
                        onClick={handleAddSubcategory}
                        variant="outlined"
                        size="small"
                    >
                        {texts.ADD_SUBCATEGORY}
                    </ActionButton>
                )}

                {/* New subcategory form */}
                <Collapse
                    in={!!newSubcategoryForCategory}
                    sx={theme => ({
                        border: `2px solid ${theme.palette.grey[400]}`,
                        borderRadius: '5px',
                        padding: 2,
                        marginY: 1,
                    })}
                >
                    {!!newSubcategoryForCategory && (
                        <SubcategoryForm
                            subcategory={null}
                            categoryId={category?.id}
                            onCancel={() => {
                                setNewSubcategoryForCategory(null);
                            }}
                            onSave={handleAddSubcategoryToForm}
                        />
                    )}
                </Collapse>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                <icons.CloseThick
                    sx={{ cursor: 'pointer', '&:hover': { color: 'error.main' } }}
                    onClick={onCancel}
                />
                <Loader loading={updateLoading || createLoading} size={20} marginTop={0}>
                    <icons.CheckBold
                        sx={{
                            cursor: isAnySubcategoryFormOpen ? 'not-allowed' : 'pointer',
                            color: isAnySubcategoryFormOpen ? 'grey.400' : 'inherit',
                            '&:hover': {
                                color: isAnySubcategoryFormOpen ? 'grey.400' : 'success.main',
                            },
                        }}
                        onClick={handleSubmit}
                    />
                </Loader>
            </Box>
        </React.Fragment>
    );
};

CategoryForm.propTypes = {
    category: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        private: PropTypes.bool,
        availableFrom: PropTypes.string,
        availableTo: PropTypes.string,
        shareSubcategories: PropTypes.array,
    }),
    fundId: PropTypes.number,
    onCancel: PropTypes.func,
};

export default CategoryForm;
